import { CalendarOutlined, DeleteOutlined, EditOutlined, LinkOutlined, MenuOutlined } from '@ant-design/icons'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button, List, Select } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import type { CSSProperties, ReactElement } from 'react'

export interface Item {
  value: string
  label: string | ReactElement
  key: string
  description?: string
  options?: Array<{ value: string; label: string }>
  optionValues?: string[]
  redirectToGoalConfig?: () => void
  disableDrag: boolean
}

export interface SortableListOptions {
  deletable?: boolean
  uneditable?: (name: string) => boolean
  // eslint-disable-next-line sonarjs/todo-tag
  undeletable?: string[] // TODO: 型が微妙
  bordered?: boolean
  onClick?: (item: Item) => void
  onEdit?: (item: Item) => void
}

type OnOptionChange = (argument: { item: Item; value: string[] }) => void

interface SortableElementActions {
  onDestroy?: (item: Item) => void
  onEdit?: (item: Item) => void
  onOptionChange?: OnOptionChange
}

type ItemArgument = {
  item: Item
  options?: SortableListOptions
  isActive: boolean
  disableDrag: boolean
} & SortableElementActions

const SortableItem = ({
  item,
  options,
  isActive,
  disableDrag,
  onDestroy,
  onEdit,
  onOptionChange,
  // eslint-disable-next-line complexity
}: ItemArgument): ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.key,
    disabled: disableDrag,
  })

  const style: CSSProperties = {
    transition,
    transform: CSS.Transform.toString(transform),
    cursor: disableDrag ? 'default' : 'grab',
  }

  const editable = options?.uneditable === undefined ? true : !options.uneditable(item.value)
  const deletable = !(options?.undeletable ?? []).includes(item.value) && (options?.deletable ?? true)
  const onClick = options?.onClick

  return (
    <List.Item
      ref={setNodeRef}
      className={`h-12 ${isActive ? `text-blue-500` : ``}`}
      {...(disableDrag ? {} : attributes)}
      {...(disableDrag ? {} : listeners)}
      style={style}
      actions={[
        // 集計軸での利用用途に特化している
        onOptionChange !== undefined && (item.options ?? []).length > 0 ? (
          // eslint-disable-next-line @eslint-react/no-missing-key
          <div>
            <CalendarOutlined />
            <Select
              size="small"
              style={{ width: 75, fontSize: 11 }}
              variant="borderless"
              placeholder={t(`表示形式`)}
              tagRender={(option) => <span style={{ marginRight: 1 }}>{option.label}</span>}
              mode="multiple"
              value={item.optionValues ?? []}
              options={item.options}
              onChange={(value: string[]) => {
                onOptionChange({ item, value })
              }}
            />
          </div>
        ) : undefined,
        editable && onEdit !== undefined ? (
          // eslint-disable-next-line @eslint-react/no-missing-key
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              onEdit(item)
            }}
            type="text"
          />
        ) : undefined,
        deletable && onDestroy !== undefined ? (
          // eslint-disable-next-line @eslint-react/no-missing-key
          <Button
            icon={<DeleteOutlined />}
            // danger
            onClick={() => {
              onDestroy(item)
            }}
            type="text"
          />
        ) : undefined,
      ].filter(isNotNullish)}
    >
      {onClick === undefined ? (
        // <Space className="hover:opacity-75">
        <>
          {!disableDrag && <MenuOutlined className="pr-3 hover:opacity-75" />}
          <span className="w-full text-left hover:opacity-75">{item.label}</span>
          {/* </Space> */}
        </>
      ) : (
        <>
          {!disableDrag && <MenuOutlined className="pr-3" />}
          <Button
            className={`w-full cursor-pointer hover:opacity-75`}
            onClick={() => {
              onClick(item)
            }}
            style={{ textAlign: 'left', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis' }}
            type="text"
          >
            <span className={isActive ? `text-blue-500` : ``} style={{ display: 'contents' }}>
              {item.label}
            </span>
          </Button>
        </>
      )}
      {item.redirectToGoalConfig !== undefined && (
        <Button
          type="text"
          icon={<LinkOutlined />}
          onClick={() => {
            // undefinedになることはないが型推論ができてない
            item.redirectToGoalConfig?.()
          }}
        />
      )}
    </List.Item>
  )
}

type Argument = {
  items: Item[]
  dragging: boolean
  className?: string
  options?: SortableListOptions
  value?: string
  loading: boolean
  axis?: 'x' | 'y'
  lockAxis?: 'x' | 'y'
  lockToContainerEdges?: boolean
  lockOffset?: string
  onSortEnd?: (targets?: { oldIndex: number; newIndex: number }) => void
  onSortStart?: () => void
} & SortableElementActions

export const SortableList = ({
  items,
  dragging,
  className,
  options,
  value,
  loading,
  onDestroy,
  onEdit,
  onOptionChange,
  onSortEnd,
  onSortStart,
}: Argument): ReactElement => {
  const itemIds: UniqueIdentifier[] = items.map((item) => item.key)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  )

  const onDragEnd = ({ active, over }: DragEndEvent): void => {
    if (over !== null && onSortEnd !== undefined) {
      const oldIndex = itemIds.indexOf(active.id)
      const newIndex = itemIds.indexOf(over.id)
      onSortEnd({ oldIndex, newIndex })
    }
  }

  const onDragCancel = (): void => {
    onSortEnd?.()
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onSortStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <List
          loading={loading}
          bordered={options?.bordered ?? true}
          className={`${className ?? ''} overflow-auto`}
          // overflow: 'clip'を指定することでドラッグ時にスクロールが機能するようになる
          style={dragging ? { cursor: 'grabbing' } : { cursor: 'grab', overflow: 'clip' }}
          dataSource={items}
          renderItem={(item) => (
            <SortableItem
              key={`item-${item.key}`}
              item={item}
              onDestroy={onDestroy}
              onEdit={onEdit}
              onOptionChange={onOptionChange}
              options={options}
              isActive={value === item.value}
              disableDrag={item.disableDrag}
            />
          )}
        />
      </SortableContext>
    </DndContext>
  )
}
